import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Search, Filter, Eye, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import { locList, locTatSettings, locWhatsappSent } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { LocWhatsappModal } from "@/components/LocWhatsappModal";

const STAGE_OPTIONS = [
  ["awaiting_loc1", "Awaiting LOC 1"], ["loc1_generated", "LOC 1 Generated"], ["loc1_sent", "LOC 1 Sent"],
  ["loc2_due", "LOC 2 Due"], ["loc2_generated", "LOC 2 Generated"], ["loc2_sent", "LOC 2 Sent"],
  ["loc3_due", "LOC 3 Due"], ["loc3_generated", "LOC 3 Generated"], ["loc3_sent", "LOC 3 Sent"],
  ["repair_resumed", "Repair Resumed"], ["closed", "Closed"],
];

const REASON_LABELS = {
  vehicle_ready: "Vehicle Ready – Not Collected",
  no_work_started: "No Work Started",
};

const STAGE_TONES = {
  awaiting_loc1: "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]",
  loc1_generated: "bg-sky-50 text-sky-700 border-sky-200",
  loc1_sent: "bg-blue-50 text-blue-700 border-blue-200",
  loc2_due: "bg-amber-50 text-amber-700 border-amber-200",
  loc2_generated: "bg-sky-50 text-sky-700 border-sky-200",
  loc2_sent: "bg-blue-50 text-blue-700 border-blue-200",
  loc3_due: "bg-orange-50 text-orange-700 border-orange-200",
  loc3_generated: "bg-sky-50 text-sky-700 border-sky-200",
  loc3_sent: "bg-red-50 text-red-700 border-red-200",
  repair_resumed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]",
};

const Filt = ({ label, children }) => (
  <label className="flex flex-col gap-0.5">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#747684]">{label}</span>
    {children}
  </label>
);

const selectCls = "px-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]";

const DueCell = ({ row }) => {
  if (row.status !== "active") return <span className="text-[#b5b3c4]">—</span>;
  if (!row.next_loc_label) return <span className="text-[#b5b3c4]">—</span>;
  if (row.overdue_by_days != null) {
    return (
      <div className="text-[11.5px]">
        <div className="font-semibold text-red-600">{row.next_loc_label}</div>
        <div className="text-[10.5px] text-red-500">Overdue by {row.overdue_by_days} Day{row.overdue_by_days === 1 ? "" : "s"}</div>
      </div>
    );
  }
  return (
    <div className="text-[11.5px]">
      <div className="font-medium text-[#1a1b22]">{row.next_loc_label} · {fmtDate(row.due_date)}</div>
      <div className="text-[10.5px] text-[#747684]">{row.due_in_days === 0 ? "Due Today" : `Due in ${row.due_in_days} Day${row.due_in_days === 1 ? "" : "s"}`}</div>
    </div>
  );
};

const ParkingCell = ({ row }) => {
  if (!row.parking_charge_start_date) return <span className="text-[#b5b3c4]">—</span>;
  const startShort = fmtDate(row.parking_charge_start_date).slice(0, 5);
  if (row.parking_status === "scheduled") {
    return <div className="text-[11px] text-sky-700 font-medium" data-testid={`loc-parking-cell-${row.loc_number}`}>Starts {startShort}</div>;
  }
  if (row.parking_status === "active") {
    return (
      <div className="text-[11px]" data-testid={`loc-parking-cell-${row.loc_number}`}>
        <div className="font-semibold text-rose-600">₹{row.parking_charge_per_day}/day</div>
        <div className="text-[10.5px] text-rose-500">Since {startShort}</div>
      </div>
    );
  }
  return (
    <div className="text-[10.5px] text-[#747684]" data-testid={`loc-parking-cell-${row.loc_number}`}>
      ₹{row.parking_estimated_charges} total
    </div>
  );
};

const LastContactedCell = ({ at }) => {
  if (!at) return <span className="text-[#8a8c99] italic text-[11px]">Never contacted</span>;
  const days = Math.max(0, Math.floor((Date.now() - new Date(at).getTime()) / 86400000));
  return (
    <div className="text-[11.5px]">
      <div className="font-medium text-[#1a1b22]">{fmtDate(at)}</div>
      <div className="text-[10.5px] text-[#747684]">{days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} ago`}</div>
    </div>
  );
};

const LocTracker = () => {
  const { currentUser, locations, currentLocation } = useApp();
  const navigate = useNavigate();

  const allowed = useMemo(
    () => locations.filter((l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)),
    [locations, currentUser],
  );

  const [q, setQ] = useState("");
  const [sourceType, setSourceType] = useState("ALL");
  const [locReason, setLocReason] = useState("ALL");
  const [stage, setStage] = useState("ALL");
  const [status, setStatus] = useState("active");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workshopContact, setWorkshopContact] = useState("");
  const [parkingPerDay, setParkingPerDay] = useState("");
  const [whatsappRow, setWhatsappRow] = useState(null);
  const [sortBy, setSortBy] = useState(null); // null | "last_contacted_at"
  const [sortDir, setSortDir] = useState("asc");
  const [quickFilter, setQuickFilter] = useState(null); // null | "due_today" | "overdue" | "parking_active"
  const canLocWrite = can(currentUser, "loc_write");

  useEffect(() => {
    locTatSettings().then((s) => {
      setWorkshopContact(s.workshop_contact_number || "");
      setParkingPerDay(s.parking_charge_per_day || "");
    }).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const p = { status };
      if (q) p.q = q;
      if (currentLocation && currentLocation !== "ALL") p.location_code = currentLocation;
      if (sourceType !== "ALL") p.source_type = sourceType;
      if (locReason !== "ALL") p.loc_reason = locReason;
      if (stage !== "ALL") p.stage = stage;
      const res = await locList(p);
      setRows(res?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load LOC Tracker");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, currentLocation, sourceType, locReason, stage, status]);

  const handleWhatsappMarkSent = async (payload) => {
    try {
      await locWhatsappSent(whatsappRow.id, { ...payload, actor: currentUser?.name, actor_role: currentUser?.role });
      toast.success("Marked as WhatsApp Sent.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log WhatsApp Sent");
      throw e;
    }
  };

  const toggleSort = (col) => {
    if (sortBy !== col) { setSortBy(col); setSortDir("asc"); }
    else setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortedRows = useMemo(() => {
    const filtered = !quickFilter ? rows : rows.filter((r) => {
      if (quickFilter === "due_today") return r.status === "active" && r.due_in_days === 0;
      if (quickFilter === "overdue") return r.status === "active" && r.overdue_by_days != null;
      if (quickFilter === "parking_active") return r.parking_status === "active";
      return true;
    });
    if (sortBy !== "last_contacted_at") return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a.last_contacted_at || ""; // never-contacted sorts first on "asc" (oldest)
      const bv = b.last_contacted_at || "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortBy, sortDir, quickFilter]);

  return (
    <div className="space-y-3" data-testid="loc-tracker-page">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-[20px] font-semibold text-yamaha-800 leading-none tracking-tight">LOC Tracker</h1>
          <p className="text-[11.5px] text-[#747684] mt-1">
            Vehicles escalated from Pending/Accidental Vehicles where the customer is not responding or not collecting.
          </p>
        </div>
      </header>

      <div className="bg-white border border-[#e2e1eb] rounded-lg p-2.5 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1.5 text-[#747684] pb-1.5">
          <Filter className="w-3.5 h-3.5" />
          <span className="text-[11.5px] font-semibold text-[#444653]">Filters:</span>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-sm pb-1.5">
          <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-2" />
          <input
            data-testid="loc-tracker-search"
            placeholder="Customer, Mobile, Reg No, Chassis, JC, AV No..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]"
          />
        </div>
        <Filt label="Source">
          <select data-testid="loc-tracker-source-filter" value={sourceType} onChange={(e) => setSourceType(e.target.value)} className={selectCls}>
            <option value="ALL">All</option>
            <option value="pv">Pending Vehicle</option>
            <option value="av">Accidental Vehicle</option>
          </select>
        </Filt>
        <Filt label="LOC Reason">
          <select data-testid="loc-tracker-reason-filter" value={locReason} onChange={(e) => setLocReason(e.target.value)} className={selectCls}>
            <option value="ALL">All</option>
            <option value="vehicle_ready">Vehicle Ready – Not Collected</option>
            <option value="no_work_started">No Work Started</option>
          </select>
        </Filt>
        <Filt label="Stage">
          <select data-testid="loc-tracker-stage-filter" value={stage} onChange={(e) => setStage(e.target.value)} className={selectCls}>
            <option value="ALL">All</option>
            {STAGE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Filt>
        <Filt label="Status">
          <select data-testid="loc-tracker-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
            <option value="active">Active only</option>
            <option value="repair_resumed">Repair Resumed</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </Filt>
        <div className="ml-auto pb-1.5 text-[10.5px] text-[#747684]">
          {loading ? "Loading…" : `${rows.length} case${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="loc-tracker-quick-filters">
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] mr-0.5">Quick:</span>
        {[
          ["due_today", "Due Today"],
          ["overdue", "Overdue"],
          ["parking_active", "Parking Charges Active"],
        ].map(([v, l]) => (
          <button
            key={v} data-testid={`loc-tracker-quickfilter-${v}`}
            onClick={() => setQuickFilter((cur) => (cur === v ? null : v))}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              quickFilter === v
                ? "bg-yamaha-700 text-white border-yamaha-700"
                : "bg-white text-[#444653] border-[#e2e1eb] hover:bg-[#faf8ff]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#e2e1eb] rounded-lg overflow-x-auto" data-testid="loc-tracker-table">
        <table className="w-full text-[12px]">
          <thead className="bg-[#fafafd] border-b border-[#e2e1eb] text-[#747684]">
            <tr>
              <th className="px-3 py-2 text-left whitespace-nowrap">Customer</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Vehicle</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Reg No.</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Source</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">LOC Reason</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Trigger Date</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Ready Date</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Current Stage</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Next LOC Due</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Parking Charges</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">
                <button data-testid="loc-tracker-sort-last-contacted" onClick={() => toggleSort("last_contacted_at")}
                  className="inline-flex items-center gap-1 hover:text-[#1a1b22]" title="Sort by who hasn't been contacted the longest">
                  Last Contacted
                  {sortBy === "last_contacted_at" && <span>{sortDir === "asc" ? "▲" : "▼"}</span>}
                </button>
              </th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Assigned User</th>
              <th className="px-3 py-2 text-left whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.id} data-testid={`loc-tracker-row-${r.loc_number}`} className="border-b border-[#f1f0f7] hover:bg-[#faf8ff]">
                <td className="px-3 py-2">
                  <div className="font-medium text-[#1a1b22]">{r.customer_name || "—"}</div>
                  <div className="text-[10.5px] text-[#747684]">{r.mobile_number || ""}</div>
                </td>
                <td className="px-3 py-2">{r.vehicle_model || "—"}</td>
                <td className="px-3 py-2 font-mono">{r.registration_number || "—"}</td>
                <td className="px-3 py-2">
                  <span className="px-1.5 py-0.5 rounded text-[10.5px] font-semibold uppercase bg-[#f1f0f7] text-[#444653]">
                    {r.source_type}
                  </span>
                  <div className="text-[10.5px] text-[#747684] mt-0.5">{r.job_card_no || r.av_number || ""}</div>
                </td>
                <td className="px-3 py-2">{REASON_LABELS[r.loc_reason] || r.loc_reason}</td>
                <td className="px-3 py-2">{fmtDate(r.trigger_date)}</td>
                <td className="px-3 py-2">{r.ready_date ? fmtDate(r.ready_date) : "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${STAGE_TONES[r.stage] || ""}`}>
                    {r.stage_label}
                  </span>
                </td>
                <td className="px-3 py-2"><DueCell row={r} /></td>
                <td className="px-3 py-2"><ParkingCell row={r} /></td>
                <td className="px-3 py-2"><LastContactedCell at={r.last_contacted_at} /></td>
                <td className="px-3 py-2">{r.assigned_user || "—"}</td>
                <td className="px-3 py-2">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => navigate(`/loc/${r.id}`)}
                      data-testid={`loc-tracker-view-${r.loc_number}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-yamaha-700 bg-yamaha-50 hover:bg-yamaha-100"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                    {canLocWrite && r.status === "active" && r.mobile_number && (
                      <button
                        onClick={() => setWhatsappRow(r)}
                        data-testid={`loc-tracker-whatsapp-${r.loc_number}`}
                        title="WhatsApp this customer"
                        className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {sortedRows.length === 0 && !loading && (
              <tr>
                <td colSpan={13} className="px-3 py-10 text-center text-[#747684]">
                  No LOC cases found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {whatsappRow && (
        <LocWhatsappModal
          caseObj={whatsappRow} workshopContact={workshopContact} defaultParkingPerDay={parkingPerDay}
          onClose={() => setWhatsappRow(null)} onMarkSent={handleWhatsappMarkSent}
        />
      )}
    </div>
  );
};

export default LocTracker;
